import { notificationService } from '@shared/services/notification-service';
import type { ClipboardExportConfig } from '@features/service-form/service-form-types';

type FormControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export class FormClipboardExporter {
  constructor(
    private form: HTMLFormElement,
    private config: ClipboardExportConfig
  ) {}

  attachButton(): HTMLButtonElement | null {
    const submitButton = this.form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!submitButton || this.form.querySelector('[data-copy-form]')) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.copyForm = '';
    button.className = 'ea-service-copy-button';
    button.textContent = this.config.buttonLabel ?? '复制已填写信息到剪贴板';
    button.addEventListener('click', () => {
      void this.copy();
    });

    submitButton.insertAdjacentElement('afterend', button);

    if (this.config.recipientEmail) {
      const hint = document.createElement('p');
      hint.className = 'ea-service-copy-hint';
      hint.textContent = `如提交异常，可复制填写信息并邮件发送至：${this.config.recipientEmail}`;
      button.insertAdjacentElement('afterend', hint);
    }

    return button;
  }

  async copy(): Promise<void> {
    const text = this.buildText();

    try {
      await this.writeText(text);
      notificationService.success('已复制填写信息，可以粘贴到邮件中。');
    } catch {
      this.showManualCopy(text);
    }
  }

  buildText(): string {
    const lines = [`Easy Aussie - ${this.config.title}`, `生成时间：${this.formatGeneratedAt()}`];

    if (this.config.recipientEmail) {
      lines.push(`收件邮箱：${this.config.recipientEmail}`);
    }

    this.form.querySelectorAll<HTMLElement>('.ea-service-form-section').forEach(section => {
      if (section.querySelector('button[type="submit"]')) return;

      const sectionLines = this.collectSection(section);
      if (sectionLines.length === 0) return;

      const heading = section.querySelector<HTMLElement>(':scope > h2');
      lines.push('', `【${this.cleanText(heading?.textContent) || '填写信息'}】`, ...sectionLines);
    });

    const fileInputs = Array.from(
      this.form.querySelectorAll<HTMLInputElement>('input[type="file"]')
    ).filter(input => input.files?.length);
    if (fileInputs.length > 0) {
      lines.push('', '【附件提示】', '文件无法复制，请在发送邮件时另行添加附件。');
    }

    return lines.join('\n');
  }

  private collectSection(section: HTMLElement): string[] {
    const lines: string[] = [];
    const repeatedItems = Array.from(section.querySelectorAll<HTMLElement>('[data-group-item]'));
    const subsectionItems = Array.from(
      section.querySelectorAll<HTMLElement>('[data-export-subsection]')
    );

    this.collectControls(section, control => {
      if (control.closest('[data-group-item], [data-export-subsection]')) return;
      const line = this.controlToLine(control, section);
      if (line) lines.push(line);
    });

    repeatedItems.forEach((item, index) => {
      const itemLines: string[] = [];
      this.collectControls(item, control => {
        const line = this.controlToLine(control, item);
        if (line) itemLines.push(line);
      });
      if (itemLines.length > 0) {
        lines.push(`-- 第 ${index + 1} 项 --`, ...itemLines);
      }
    });

    subsectionItems.forEach(item => {
      const itemLines: string[] = [];
      this.collectControls(item, control => {
        const line = this.controlToLine(control, item);
        if (line) itemLines.push(line);
      });
      if (itemLines.length > 0) {
        lines.push(`-- ${item.dataset.exportSubsection || '补充信息'} --`, ...itemLines);
      }
    });

    return lines;
  }

  private collectControls(root: HTMLElement, visit: (control: FormControl) => void): void {
    const processedChoiceNames = new Set<string>();

    root.querySelectorAll<FormControl>('input, textarea, select').forEach(control => {
      if (!this.shouldExport(control)) return;

      if (
        control instanceof HTMLInputElement &&
        (control.type === 'radio' || control.type === 'checkbox')
      ) {
        if (processedChoiceNames.has(control.name)) return;
        processedChoiceNames.add(control.name);
      }

      visit(control);
    });
  }

  private controlToLine(control: FormControl, scope: HTMLElement): string | null {
    const label = this.findLabel(control, scope);
    const value = this.readValue(control, scope);
    if (!label || !value) return null;
    return `${label}：${value}`;
  }

  private findLabel(control: FormControl, scope: HTMLElement): string {
    if (
      control instanceof HTMLInputElement &&
      (control.type === 'radio' || control.type === 'checkbox')
    ) {
      const choiceRow = control.closest('.ea-service-choice-row');
      const question = choiceRow?.querySelector<HTMLElement>(':scope > .ea-service-form-label');
      if (question) return this.cleanText(question.textContent);
    }

    const wrapper = control.parentElement;
    const directLabel = wrapper?.querySelector<HTMLElement>(':scope > .ea-service-form-label');
    if (directLabel) return this.cleanText(directLabel.textContent);

    if (control.id) {
      const explicitLabel = scope.querySelector<HTMLLabelElement>(`label[for="${control.id}"]`);
      if (explicitLabel) return this.cleanText(explicitLabel.textContent);
    }

    return control.name.replace(/\[\]$/, '');
  }

  private readValue(control: FormControl, scope: HTMLElement): string {
    if (control instanceof HTMLSelectElement) {
      if (!control.value) return '';
      return this.cleanText(control.selectedOptions[0]?.textContent);
    }

    if (
      control instanceof HTMLInputElement &&
      (control.type === 'radio' || control.type === 'checkbox')
    ) {
      const choices = Array.from(scope.querySelectorAll<HTMLInputElement>('input')).filter(
        input => input.name === control.name && input.checked
      );

      return choices
        .map(input => {
          const optionLabel = input.closest('label');
          return this.cleanText(optionLabel?.textContent) || input.value;
        })
        .join('、');
    }

    return control.value.trim();
  }

  private shouldExport(control: FormControl): boolean {
    if (!control.name || control.disabled || control.closest('[hidden]')) return false;
    if (
      control instanceof HTMLInputElement &&
      (control.type === 'hidden' || control.type === 'file')
    ) {
      return false;
    }
    return true;
  }

  private async writeText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Clipboard copy failed');
  }

  private showManualCopy(text: string): void {
    const existing = document.getElementById('form-copy-dialog');
    existing?.remove();

    const dialog = document.createElement('div');
    dialog.id = 'form-copy-dialog';
    dialog.className = 'ea-service-copy-dialog';
    dialog.innerHTML = `
      <div class="ea-service-copy-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="form-copy-title">
        <h2 id="form-copy-title">请手动复制以下内容</h2>
        <textarea readonly></textarea>
        <button type="button">关闭</button>
      </div>
    `;

    const textarea = dialog.querySelector<HTMLTextAreaElement>('textarea');
    const closeButton = dialog.querySelector<HTMLButtonElement>('button');
    if (!textarea || !closeButton) return;

    textarea.value = text;
    closeButton.addEventListener('click', () => dialog.remove());
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.remove();
    });

    document.body.appendChild(dialog);
    textarea.focus();
    textarea.select();
    notificationService.warning('自动复制失败，请长按或选择文本后手动复制。');
  }

  private formatGeneratedAt(): string {
    return new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Australia/Perth',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date());
  }

  private cleanText(value?: string | null): string {
    return (value ?? '').replace(/\s+/g, ' ').trim();
  }
}
